import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";
import NotFound from "./NotFound";
import { useT } from "../i18n/LanguageContext";
import { track } from "../lib/analytics";
import { getPostBySlug } from "../lib/blog";

const SITE_URL = "https://gfirem.com";

/**
 * Injects per-post <title>, meta description, canonical, og/twitter meta
 * and a BlogPosting JSON-LD block at mount; restores previous state on
 * unmount. Runtime SEO is a defensive layer — bots see the version baked
 * into build/blog/<slug>/index.html by scripts/prerender-seo.js.
 */
function usePostHead(post) {
    useEffect(() => {
        if (!post) return;

        const title = `${post.title} | gfirem`;
        const description = post.description;
        const canonical = `${SITE_URL}/blog/${post.slug}`;
        const ogImage = `${SITE_URL}${post.ogImage || "/og/default.jpg"}`;

        // Snapshot previous head values so we can restore them on unmount.
        const prevTitle = document.title;
        document.title = title;

        const setMetaProp = (selector, attr, value) => {
            const el = document.head.querySelector(selector);
            if (!el) return null;
            const prev = el.getAttribute(attr);
            el.setAttribute(attr, value);
            return { el, attr, prev };
        };

        const patches = [
            setMetaProp('meta[name="description"]', "content", description),
            setMetaProp('link[rel="canonical"]', "href", canonical),
            setMetaProp('meta[property="og:type"]', "content", "article"),
            setMetaProp('meta[property="og:title"]', "content", title),
            setMetaProp('meta[property="og:description"]', "content", description),
            setMetaProp('meta[property="og:url"]', "content", canonical),
            setMetaProp('meta[property="og:image"]', "content", ogImage),
            setMetaProp('meta[name="twitter:title"]', "content", title),
            setMetaProp('meta[name="twitter:description"]', "content", description),
            setMetaProp('meta[name="twitter:image"]', "content", ogImage),
        ].filter(Boolean);

        // Inject BlogPosting JSON-LD.
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-blog-jsonld", post.slug);
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "@id": `${canonical}#article`,
            headline: post.title,
            description: post.description,
            datePublished: post.date,
            dateModified: post.date,
            inLanguage: post.lang,
            author: {
                "@type": "Person",
                name: post.author,
                url: `${SITE_URL}/#person`,
            },
            publisher: { "@id": `${SITE_URL}/#organization` },
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
            image: ogImage,
            keywords: (post.tags || []).join(", "),
            url: canonical,
        });
        document.head.appendChild(script);

        return () => {
            document.title = prevTitle;
            patches.forEach(({ el, attr, prev }) => {
                if (prev !== null && prev !== undefined) el.setAttribute(attr, prev);
            });
            script.remove();
        };
    }, [post]);
}

export default function BlogPost() {
    const { slug } = useParams();
    const { t } = useT();
    const post = getPostBySlug(slug);

    usePostHead(post);

    useEffect(() => {
        if (post) {
            track("blog_post_viewed", {
                slug: post.slug,
                lang: post.lang,
                reading_minutes: post.readingMinutes,
            });
        }
    }, [post]);

    if (!post) return <NotFound />;

    return (
        <main
            className="min-h-screen bg-[var(--sand)] text-[var(--ink)]"
            data-testid="blog-post-page"
        >
            <Nav />

            <article className="mx-auto max-w-3xl px-6 pt-24 sm:pt-32 pb-16">
                <div className="mb-6">
                    <Link
                        to="/blog"
                        data-testid="blog-post-back"
                        data-cta-source="blog-post-back"
                        className="font-mono text-xs tracking-widest uppercase text-[var(--ink)]/60 hover:text-[var(--ink)] transition-colors"
                    >
                        ← {t("blog.backToIndex") || "All field notes"}
                    </Link>
                </div>

                <p
                    className="overline"
                    data-testid="blog-post-overline"
                >
                    <time dateTime={post.date}>{post.date}</time>
                    <span className="mx-3 text-[var(--ink)]/30">·</span>
                    {post.readingMinutes} min read
                </p>

                <h1
                    className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight"
                    data-testid="blog-post-title"
                >
                    {post.title}
                </h1>

                <p
                    className="mt-6 text-lg sm:text-xl text-[var(--ink)]/80 leading-relaxed"
                    data-testid="blog-post-description"
                >
                    {post.description}
                </p>

                <div className="mt-8 flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                        <span
                            key={tag}
                            className="text-[0.65rem] font-mono tracking-wide uppercase border border-warm text-ink/60 px-2 py-0.5"
                        >
                            {tag}
                        </span>
                    ))}
                </div>

                <div
                    className="prose-blog mt-12 border-t border-[var(--ink)]/20 pt-12"
                    data-testid="blog-post-body"
                >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {post.content}
                    </ReactMarkdown>
                </div>

                <div className="mt-16 border-t border-[var(--ink)]/20 pt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <p className="font-mono text-xs uppercase tracking-widest text-[var(--ink)]/60">
                        {t("blog.writtenBy") || "Written by"} {post.author}
                    </p>
                    <Link
                        to="/blog"
                        data-testid="blog-post-more"
                        className="font-mono text-xs uppercase tracking-widest text-terracotta hover:opacity-70 transition-opacity"
                    >
                        {t("blog.moreNotes") || "Read more field notes →"}
                    </Link>
                </div>
            </article>

            <Footer />
        </main>
    );
}
